// components/Industries.tsx
"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import IndustryCarousel from "./IndustryCarousel";
import { shades } from "@/styles/theme";
import PopoverForm from "./PopoverForm";

const industries = [
  {
    icon: <StorefrontIcon sx={{ fontSize: 40 }} />,
    title: "E-commerce & Retail",
    description:
      "We build conversion-optimized online stores and automate your backend operations — from inventory syncs to real-time order tracking and customer engagement.",
    features: [
      "Headless e-commerce systems",
      "Automated inventory management",
      "Payment & shipping integrations",
      "Customer loyalty automation",
    ],
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    title: "SaaS & Digital Platforms",
    description:
      "We help SaaS companies in Europe design scalable platforms, automate subscription workflows, and integrate analytics dashboards for data-driven decisions.",
    features: [
      "Subscription & billing automation",
      "User dashboards & portals",
      "API-first architecture",
      "Real-time analytics & reporting",
    ],
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    title: "Fintech & Payments",
    description:
      "Secure, compliant, and performance-driven fintech solutions that integrate automation for faster onboarding, KYC processes, and reporting compliance.",
    features: [
      "Automated KYC & onboarding",
      "Secure payment gateways",
      "Compliance-ready frameworks (GDPR/PSD2)",
      "Data analytics dashboards",
    ],
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  {
    icon: <ApartmentIcon sx={{ fontSize: 40 }} />,
    title: "SMEs & Enterprises",
    description:
      "We empower small and medium enterprises with digital tools and workflow automation systems that improve efficiency, visibility, and profitability.",
    features: [
      "Process automation systems",
      "CRM & ERP integration",
      "Digital transformation strategy",
      "Workflow optimization",
    ],
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
    title: "Agencies & Consultants",
    description:
      "We partner with creative and marketing agencies to deliver white-label automation and client portal solutions that simplify project delivery.",
    features: [
      "White-label automation systems",
      "Client management portals",
      "Project tracking tools",
      "Performance dashboards",
    ],
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  },
];

export default function Industries() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            component="p"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Industries We Serve
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            color={shades.primary[400]}
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Specialized solutions for diverse industries, delivering measurable
            results and driving growth
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {industries.map((industry, index) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: "white",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Icon with gradient background */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: industry.gradient,
                      color: "white",
                      mb: 3,
                    }}
                  >
                    {industry.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600, color: shades.primary[300] }}
                  >
                    {industry.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color={`${shades.primary[500]}`}
                    paragraph
                    sx={{ lineHeight: 1.4 }}
                  >
                    {industry.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {industry.features.map((feature, featureIndex) => (
                      <Typography
                        key={featureIndex}
                        variant="body1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          color: shades.primary[400],
                          fontWeight: 500,
                          "&:before": {
                            content: '"▸"',
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            mr: 1,
                          },
                        }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Box>

                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                  >
                    View Case Studies
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <IndustryCarousel />

        {/* CTA Section */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography
            variant="h6"
            component={"h4"}
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Don&apos;t see your industry? We customize solutions for unique
            business needs.
          </Typography>
          <PopoverForm
            triggerElement={
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Discuss Your Project
              </Button>
            }
          />
        </Box>
      </Container>
    </Box>
  );
}
